// The §13.2 synthesis recipe kit. A recipe receives a `Kit` bound to one OfflineAudioContext and
// one variant: seeded randomness, sources (osc, noise, pluck, crackle, fm), shaping (env, pitchEnv,
// filters, formant, distort) and `out`, the variant's DC-blocked output. Envelopes are rendered
// as value curves that start and end at exactly zero, so no recipe can click.
import { mulberry32 } from '../util';

export type NoiseColor = 'white' | 'pink' | 'brown';
export type Vowel = 'a' | 'e' | 'i' | 'o' | 'u';

/** Attack / decay / sustain level (0..1) / release, seconds; `hold` keeps the sustain. */
export interface Adsr { a: number; d: number; s: number; r: number; hold?: number; peak?: number; at?: number }

/** §13.2 formant pairs (F1/F2 Hz). */
export const FORMANTS: Readonly<Record<Vowel, readonly [number, number]>> = {
  a: [800, 1150], e: [400, 1600], i: [280, 2250], o: [450, 800], u: [325, 700],
};

export interface Kit {
  readonly ctx: BaseAudioContext;
  /** The variant's output (a 15 Hz DC blocker in front of the render destination). */
  readonly out: AudioNode;
  readonly variant: number;
  readonly sr: number;
  /** Rendered length in seconds. */
  readonly dur: number;
  rand(): number;
  range(lo: number, hi: number): number;
  /** ±`amount` (fraction) around 1, different per variant. */
  jitter(amount: number): number;
  pick<T>(items: readonly T[]): T;
  osc(type: OscillatorType, freq: number, at?: number, stop?: number): OscillatorNode;
  wave(real: readonly number[], freq: number, at?: number): OscillatorNode;
  noise(color?: NoiseColor, at?: number, stop?: number): AudioBufferSourceNode;
  env(a: Adsr): GainNode;
  gain(v: number): GainNode;
  pitchEnv(param: AudioParam, from: number, to: number, time: number, curve?: 'exp' | 'lin', at?: number): void;
  bp(freq: number, q?: number): BiquadFilterNode;
  lp(freq: number, q?: number): BiquadFilterNode;
  hp(freq: number, q?: number): BiquadFilterNode;
  peaking(freq: number, q: number, gainDb: number): BiquadFilterNode;
  fm(carrier: number, ratio: number, index: number, decay: number, at?: number, type?: OscillatorType): OscillatorNode;
  pluck(freq: number, dur: number, brightness?: number, at?: number): AudioBufferSourceNode;
  formant(vowel: Vowel, q?: number): { input: AudioNode; output: AudioNode; f1: BiquadFilterNode; f2: BiquadFilterNode };
  crackle(density: number, dur: number, at?: number): AudioBufferSourceNode;
  distort(amount: number): WaveShaperNode;
  lfo(rate: number, depth: number, target: AudioParam, type?: OscillatorType, at?: number): OscillatorNode;
  /** Connects nodes in sequence and returns the last. */
  chain(...nodes: AudioNode[]): AudioNode;
  /** chain(...nodes) → out. */
  toOut(...nodes: AudioNode[]): void;
}

// ---- Shared buffers --------------------------------------------------------------------------

const NOISE_SECONDS = 2;
const noiseCache = new Map<string, AudioBuffer>();

function makeBuffer(ctx: BaseAudioContext, length: number): AudioBuffer {
  return ctx.createBuffer(1, Math.max(1, length), ctx.sampleRate);
}

/**
 * 2 s of seeded noise per colour and sample rate (shared by every render), seamlessly loopable:
 * sources loop it, and for correlated noise (brown, pink's low end) a plain wrap would be a step.
 * The process runs 50 ms past the end and that continuation is crossfaded into the head, so the
 * sample after the last one is the process's own next sample.
 */
export function noiseBuffer(ctx: BaseAudioContext, color: NoiseColor): AudioBuffer {
  const key = `${color}@${ctx.sampleRate}`;
  const hit = noiseCache.get(key);
  if (hit) return hit;
  const n = Math.round(ctx.sampleRate * NOISE_SECONDS);
  const fade = Math.round(ctx.sampleRate * 0.05);
  const raw = new Float32Array(n + fade);
  const r = mulberry32(color === 'white' ? 11 : color === 'pink' ? 23 : 37);
  if (color === 'white') {
    for (let i = 0; i < raw.length; i++) raw[i] = r() * 2 - 1;
  } else if (color === 'pink') {
    // Paul Kellet's economy pink filter.
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < raw.length; i++) {
      const w = r() * 2 - 1;
      b0 = 0.99765 * b0 + w * 0.099046;
      b1 = 0.963 * b1 + w * 0.2965164;
      b2 = 0.57 * b2 + w * 1.0526913;
      raw[i] = (b0 + b1 + b2 + w * 0.1848) * 0.2;
    }
  } else {
    let last = 0;
    for (let i = 0; i < raw.length; i++) {
      last = (last + 0.02 * (r() * 2 - 1)) / 1.02;
      raw[i] = last * 3.5;
    }
  }
  const buf = makeBuffer(ctx, n);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = raw[i];
  for (let i = 0; i < fade; i++) {
    const w = i / fade;
    d[i] = raw[i] * w + raw[n + i] * (1 - w);
  }
  // Remove DC so loops and filters start from silence.
  let mean = 0;
  for (let i = 0; i < n; i++) mean += d[i];
  mean /= n;
  for (let i = 0; i < n; i++) d[i] -= mean;
  noiseCache.set(key, buf);
  return buf;
}

/** Envelope curve: linear attack, exponential decay to sustain, hold, exponential release to 0. */
export function envelopeCurve(e: Adsr, points?: number): { curve: Float32Array; total: number } {
  const hold = e.hold ?? 0;
  const peak = e.peak ?? 1;
  const total = Math.max(0.002, e.a + e.d + hold + e.r);
  // 600 points/s resolves fast attacks; long envelopes (crowds, sustains) need far fewer.
  const n = Math.max(16, Math.min(4096, points ?? Math.round(total <= 1 ? total * 600 : 600 + (total - 1) * 200)));
  const curve = new Float32Array(n);
  // Normalised exponential fall from 1 (x = 0) to exactly 0 (x = 1).
  const fall = (x: number, k: number): number => (Math.exp(-k * x) - Math.exp(-k)) / (1 - Math.exp(-k));
  for (let i = 0; i < n; i++) {
    const t = (i / (n - 1)) * total;
    let v: number;
    if (t < e.a) {
      v = e.a > 0 ? t / e.a : 1;
    } else if (t < e.a + e.d) {
      v = e.s + (1 - e.s) * fall((t - e.a) / Math.max(1e-6, e.d), 5);
    } else if (t < e.a + e.d + hold) {
      v = e.s;
    } else {
      // A zero-sustain envelope has already reached 0 at the end of its decay.
      v = e.s * fall(Math.min(1, (t - e.a - e.d - hold) / Math.max(1e-6, e.r)), 4.5);
    }
    curve[i] = v * peak;
  }
  curve[0] = 0;
  curve[n - 1] = 0;
  return { curve, total };
}

/**
 * A musical saturation curve: y = tanh(g·x) / tanh(g) with drive g = 1 + 8·amount (0.1 is warmth,
 * 0.6 a fuzzy honk). Smooth everywhere, so slow zero crossings never become edges.
 */
export function saturationCurve(amount: number, n = 2048): Float32Array<ArrayBuffer> {
  const g = 1 + 8 * Math.max(0, amount);
  const norm = Math.tanh(g);
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = Math.tanh(g * x) / norm;
  }
  return c;
}

/** A staircase (bit-crush) curve with `steps` levels. */
export function staircaseCurve(steps: number, n = 2048): Float32Array<ArrayBuffer> {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = Math.round(x * steps) / steps;
  }
  return c;
}

/**
 * Karplus-Strong plucked string: a noise burst circulating through a damped, fractionally
 * delayed averaging loop. `brightness` (0..1) low-passes the excitation.
 */
export function karplusStrong(sr: number, freq: number, dur: number, brightness: number, rand: () => number): Float32Array {
  const n = Math.max(1, Math.round(sr * dur));
  const out = new Float32Array(n);
  const delay = Math.max(2, sr / freq - 0.5);
  const di = Math.floor(delay);
  const frac = delay - di;
  // Loss per period so the note decays by ~60 dB over `dur` (but never longer than a real string).
  const periods = (dur * freq);
  const g = Math.min(0.9995, 10 ** (-3 / Math.max(1, periods)));
  let lp = 0;
  const excite = Math.ceil(delay) + 1;
  for (let i = 0; i < n; i++) {
    let x = 0;
    if (i < excite) {
      const w = rand() * 2 - 1;
      lp += (w - lp) * (0.15 + 0.85 * brightness);
      x = lp;
    }
    const a = i - di >= 0 ? out[i - di] : 0;
    const b = i - di - 1 >= 0 ? out[i - di - 1] : 0;
    const c = i - di - 2 >= 0 ? out[i - di - 2] : 0;
    const delayed = a * (1 - frac) + b * frac;
    const delayed1 = b * (1 - frac) + c * frac;
    out[i] = x + g * 0.5 * (delayed + delayed1);
  }
  // Fade the tail to zero.
  const fade = Math.min(n, Math.round(sr * 0.01));
  for (let i = 0; i < fade; i++) out[n - 1 - i] *= i / fade;
  return out;
}

export function createKit(ctx: BaseAudioContext, variant: number, out: AudioNode, dur: number, seed: number): Kit {
  const rand = mulberry32(seed * 7919 + variant * 104729 + 17);
  const sr = ctx.sampleRate;
  const end = dur;

  const kit: Kit = {
    ctx, out, variant, sr, dur,
    rand,
    range: (lo, hi) => lo + (hi - lo) * rand(),
    jitter: (amount) => 1 + (variant - 1) * amount + (rand() * 2 - 1) * amount * 0.25,
    pick: (items) => items[Math.floor(rand() * items.length) % items.length],
    osc(type, freq, at = 0, stop = end) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = freq;
      o.start(at);
      o.stop(Math.max(at + 0.001, stop));
      return o;
    },
    wave(real, freq, at = 0) {
      const o = ctx.createOscillator();
      const re = new Float32Array([0, ...real]);
      const im = new Float32Array(re.length);
      o.setPeriodicWave(ctx.createPeriodicWave(re, im, { disableNormalization: false }));
      o.frequency.value = freq;
      o.start(at);
      o.stop(end);
      return o;
    },
    noise(color = 'white', at = 0, stop = end) {
      const s = ctx.createBufferSource();
      s.buffer = noiseBuffer(ctx, color);
      s.loop = true;
      s.start(at, rand() * (NOISE_SECONDS - 0.1));
      s.stop(Math.max(at + 0.001, stop));
      return s;
    },
    env(a) {
      const g = ctx.createGain();
      g.gain.value = 0;
      const { curve, total } = envelopeCurve(a);
      g.gain.setValueCurveAtTime(curve, a.at ?? 0, total);
      return g;
    },
    gain(v) {
      const g = ctx.createGain();
      g.gain.value = v;
      return g;
    },
    pitchEnv(param, from, to, time, curve = 'exp', at = 0) {
      param.setValueAtTime(from, at);
      if (curve === 'exp' && from > 0 && to > 0) param.exponentialRampToValueAtTime(to, at + time);
      else param.linearRampToValueAtTime(to, at + time);
    },
    bp(freq, q = 1) {
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = freq;
      f.Q.value = q;
      return f;
    },
    lp(freq, q = 0.707) {
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = freq;
      f.Q.value = q;
      return f;
    },
    hp(freq, q = 0.707) {
      const f = ctx.createBiquadFilter();
      f.type = 'highpass';
      f.frequency.value = freq;
      f.Q.value = q;
      return f;
    },
    peaking(freq, q, gainDb) {
      const f = ctx.createBiquadFilter();
      f.type = 'peaking';
      f.frequency.value = freq;
      f.Q.value = q;
      f.gain.value = gainDb;
      return f;
    },
    fm(carrier, ratio, index, decay, at = 0, type = 'sine') {
      const c = kit.osc(type, carrier, at);
      const m = kit.osc('sine', carrier * ratio, at);
      const depth = ctx.createGain();
      const dev = index * carrier * ratio;
      depth.gain.setValueAtTime(dev, at);
      depth.gain.linearRampToValueAtTime(0, at + Math.max(0.005, decay));
      m.connect(depth).connect(c.frequency);
      return c;
    },
    pluck(freq, dur2, brightness = 0.7, at = 0) {
      const data = karplusStrong(sr, freq, dur2, brightness, rand);
      const buf = makeBuffer(ctx, data.length);
      buf.getChannelData(0).set(data);
      const s = ctx.createBufferSource();
      s.buffer = buf;
      s.start(at);
      return s;
    },
    formant(vowel, q = 7) {
      const [a, b] = FORMANTS[vowel];
      const input = ctx.createGain();
      const output = ctx.createGain();
      const f1 = kit.bp(a, q);
      const f2 = kit.bp(b, q * 1.3);
      const g2 = kit.gain(0.6);
      input.connect(f1).connect(output);
      input.connect(f2).connect(g2).connect(output);
      output.gain.value = 3;
      return { input, output, f1, f2 };
    },
    crackle(density, dur2, at = 0) {
      const n = Math.max(1, Math.round(sr * dur2));
      const buf = makeBuffer(ctx, n);
      const d = buf.getChannelData(0);
      const count = Math.round(density * dur2);
      // Each grain is a tiny damped sine "tick" (1.5–6 kHz) with an 8-sample raised-cosine attack: crisp, never a step.
      for (let k = 0; k < count; k++) {
        const len = 12 + Math.floor(rand() * 60);
        const i0 = Math.floor(rand() * Math.max(1, n - len - 1));
        const amp = (0.3 + 0.7 * rand() ** 2) * (rand() < 0.5 ? -1 : 1);
        const w = (2 * Math.PI * (1500 + rand() * 4500)) / sr;
        const phase = rand() * Math.PI;
        for (let j = 0; j < len; j++) {
          const att = j < 8 ? 0.5 - 0.5 * Math.cos((Math.PI * j) / 8) : 1;
          const dec = (1 - j / len) ** 2;
          d[i0 + j] += amp * att * dec * Math.sin(phase + w * j);
        }
      }
      const s = ctx.createBufferSource();
      s.buffer = buf;
      s.start(at);
      return s;
    },
    distort(amount) {
      const w = ctx.createWaveShaper();
      w.curve = saturationCurve(amount);
      w.oversample = '2x';
      return w;
    },
    lfo(rate, depth, target, type = 'sine', at = 0) {
      const o = kit.osc(type, rate, at);
      const g = kit.gain(depth);
      o.connect(g).connect(target);
      return o;
    },
    chain(...nodes) {
      for (let i = 0; i < nodes.length - 1; i++) nodes[i].connect(nodes[i + 1]);
      return nodes[nodes.length - 1];
    },
    toOut(...nodes) {
      kit.chain(...nodes).connect(out);
    },
  };
  return kit;
}
